import React, { useRef, useState } from 'react';
import CloudUpload from '@material-ui/icons/CloudUpload';
import InsertDriveFile from '@material-ui/icons/InsertDriveFile';
import styled, { css } from 'styled-components';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/root';
import { useAddWikiInspectionMutation, useUpdateWikiInspectionMutation } from '../../../redux/rtkQuery/apiWikiInspectionSlice';
import { setSelectedJurisdiction } from '../../../redux/slices/wikiSlice';

const FileUpload = () => {
  // @ts-ignore
  const { wikiId } = useParams();
  const selectedJurisdiction = useSelector((s: RootState) => s.wikiSlice.selectedJurisdiction);
  const [addWikiInspection] = useAddWikiInspectionMutation();
  const [updateWikiInspection] = useUpdateWikiInspectionMutation();
  const dispatch = useDispatch();
  const ref = useRef<HTMLInputElement>();
  const [dragging, setDragging] = useState(false);

  const openPicker = () => {
    ref.current?.click();
  };

  const uploadFile = async (selectedFile: File) => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append('jurisdiction', wikiId);
    formData.append('inspection_codes_report', selectedFile);
    formData.append('inspection_codes_visible_filename', selectedFile.name);
    let result;
    if (selectedJurisdiction.inspection && selectedJurisdiction.inspection.id) {
      const arg = { formData, id: selectedJurisdiction.inspection.id };
      result = await updateWikiInspection(arg);
    } else {
      result = await addWikiInspection(formData);
    }
    dispatch(setSelectedJurisdiction({ ...selectedJurisdiction, inspection: result.data }));
  };

  const handleFileChange = async e => {
    const selectedFile = e.target.files?.[0];
    await uploadFile(selectedFile);
    if (ref.current) ref.current.value = '';
  };

  const handleDragOver = e => {
    e.preventDefault();
    setDragging(true);
  };
  const handleDragLeave = e => {
    e.preventDefault();
    setDragging(false);
  };
  const handleDrop = async e => {
    e.preventDefault();
    setDragging(false);
    const selectedFile = e.dataTransfer.files?.[0];
    await uploadFile(selectedFile);
  };

  const showFile = () => {
    const url = selectedJurisdiction?.inspection?.inspectionCodesReport;
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const filename = selectedJurisdiction?.inspection?.inspectionCodesVisibleFilename;

  return (
    <DropZone
      active={dragging}
      onClick={openPicker}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') openPicker(); }}
    >
      <CloudUpload />
      <DropText>
        <Primary>{dragging ? 'Drop file to upload' : 'Drag & drop or click to upload'}</Primary>
        <Secondary>PDF inspection codes report</Secondary>
      </DropText>
      {filename && (
        <FilePill
          onClick={e => {
            e.stopPropagation();
            showFile();
          }}
          title="View file"
        >
          <InsertDriveFile fontSize="small" />
          <span>{filename}</span>
        </FilePill>
      )}
      <HiddenInput ref={ref} type="file" name="file" onChange={handleFileChange} />
    </DropZone>
  );
};

export default FileUpload;

const DropZone = styled.div<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px 16px;
  border: 2px dashed var(--border);
  border-radius: 10px;
  background: var(--neutral-0);
  cursor: pointer;
  text-align: center;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    color 0.15s ease;

  svg {
    font-size: 2rem;
    color: var(--neutral-400);
    transition: color 0.15s ease;
  }

  &:hover {
    border-color: var(--primary-300);
    background: var(--primary-50);

    svg {
      color: var(--primary-600);
    }
  }

  ${({ active }) =>
    active &&
    css`
      border-color: var(--primary-600);
      background: var(--primary-50);

      svg {
        color: var(--primary-600);
      }
    `}
`;

const DropText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const Primary = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
`;

const Secondary = styled.div`
  font-size: 0.75rem;
  color: var(--neutral-500);
`;

const FilePill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--neutral-0);
  color: var(--primary-600);
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  max-width: 100%;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--primary-50);
    border-color: var(--primary-300);
  }

  svg {
    font-size: 1rem;
    color: var(--primary-600);
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 260px;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;
